import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { login, verify2FA } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../components/ui/input-otp";
import { LogIn, ArrowLeft, Shield } from "lucide-react";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

export default function Login() {
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [otpValue, setOtpValue] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await login(data);
      
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setPendingUserId(response.data.user_id);
        toast.info("Enter your 2FA code");
      } else {
        loginUser(response.data.token, response.data.user);
        toast.success("Welcome back!");
        navigate("/game");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async () => {
    if (otpValue.length !== 6) {
      toast.error("Enter complete 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await verify2FA(pendingUserId, otpValue);
      loginUser(response.data.token, response.data.user);
      toast.success("Welcome back!");
      navigate("/game");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid 2FA code");
      setOtpValue("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid-pattern flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[128px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        {/* Card */}
        <div className="glass-card rounded-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_31e6f2da-e30e-4604-a2ae-ae4870f67bdf/artifacts/m4cp44gx_passivepay_new_logo.png" 
              alt="PassivePay" 
              className="w-16 h-16 rounded-full object-cover mx-auto mb-4"
            />
            <h1 className="text-2xl font-heading font-bold">
              {requires2FA ? "Two-Factor Auth" : "Welcome Back"}
            </h1>
            <p className="text-muted-foreground mt-2">
              {requires2FA
                ? "Enter your authenticator code"
                : "Sign in to start earning"}
            </p>
          </div>

          {requires2FA ? (
            /* 2FA Form */
            <div className="space-y-6">
              <div className="flex items-center justify-center gap-2 text-primary mb-4">
                <Shield className="w-5 h-5" />
                <span className="font-medium">Google Authenticator</span>
              </div>

              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otpValue}
                  onChange={setOtpValue}
                  data-testid="otp-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="border-primary/30" />
                    <InputOTPSlot index={1} className="border-primary/30" />
                    <InputOTPSlot index={2} className="border-primary/30" />
                    <InputOTPSlot index={3} className="border-primary/30" />
                    <InputOTPSlot index={4} className="border-primary/30" />
                    <InputOTPSlot index={5} className="border-primary/30" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                onClick={handle2FAVerify}
                disabled={loading || otpValue.length !== 6}
                className="w-full neon-glow font-heading font-bold uppercase tracking-wider"
                data-testid="verify-2fa-btn"
              >
                {loading ? "Verifying..." : "Verify & Login"}
              </Button>

              <Button
                variant="ghost"
                onClick={() => {
                  setRequires2FA(false);
                  setOtpValue("");
                }}
                className="w-full"
              >
                Back to login
              </Button>
            </div>
          ) : (
            /* Login Form */
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  {...register("email")}
                  className="bg-background/50 border-white/10 focus:border-primary h-12"
                  data-testid="login-email"
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-primary hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  {...register("password")}
                  className="bg-background/50 border-white/10 focus:border-primary h-12"
                  data-testid="login-password"
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full neon-glow btn-glow font-heading font-bold uppercase tracking-wider h-12"
                data-testid="login-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-5 h-5 mr-2" /> Sign In
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Register Link */}
          {!requires2FA && (
            <p className="text-center text-muted-foreground mt-6">
              Don't have an account?{" "}
              <Link to="/register" className="text-primary hover:underline font-medium">
                Create one
              </Link>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
